import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money } from "@owners/format";
import { api } from "../api";

/**
 * Statement review (V012, D3 2026-10-27). For hotels without Meridian books,
 * each uploaded monthly P&L is read into the summary below. Nothing reaches
 * owners until a reviewer approves the month. Shown on Financial Reports to
 * Asset Managers and Admins.
 */
interface Cell { actual: number | null; budget: number | null; lastYear: number | null }
interface Stmt {
  id: string; hotelName: string; period: string; status: "extracting" | "extracted" | "approved" | "rejected" | "failed";
  warnings: string[]; error: string | null; reviewer: string | null; reviewedAt: string | null; reviewNote: string | null;
  documentId: string | null; fileName: string | null;
  rows: Array<{ key: string; label: string; kind: string; sign: string } & Cell>;
  stats: Record<"roomsAvailable" | "roomsSold", Cell>;
}
interface Res { pending: Stmt[]; recent: Stmt[]; readerConfigured: boolean }

const monthLabel = (p: string) => new Date(p + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const STATUS: Record<Stmt["status"], [string, string]> = {
  extracting: ["Reading…", "warn-pill"], extracted: ["To review", "warn-pill"], failed: ["Couldn't read", "bad-pill"],
  approved: ["Approved", "ok-pill"], rejected: ["Rejected", "bad-pill"],
};

export function StatementReview({ admin, startOpen }: { admin: boolean; startOpen: boolean }) {
  const q = useQuery({ queryKey: ["statements-review"], queryFn: () => api<Res>("/api/statements/review"), refetchInterval: (r) => (r.state.data?.pending.some((s) => s.status === "extracting") ? 3000 : false) });
  const [open, setOpen] = useState<string | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const d = q.data;
  if (!d || (!d.pending.length && !d.recent.length)) return null;
  const list = showRecent ? d.recent : d.pending;
  const current = list.find((s) => s.id === open) ?? (startOpen ? list[0] : undefined);
  return (
    <div className="card" style={{ borderColor: d.pending.length ? "var(--gold)" : undefined }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>Monthly statements to review</h2>
        <div className="seg">
          <button className={!showRecent ? "on" : ""} onClick={() => { setShowRecent(false); setOpen(null); }}>Waiting · {d.pending.length}</button>
          <button className={showRecent ? "on" : ""} onClick={() => { setShowRecent(true); setOpen(null); }}>Recent decisions</button>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        For hotels without Meridian books, each uploaded P&L is read into this summary. Owners see a month only after it's approved here.
        {!d.readerConfigured && <strong className="error"> Statement reading isn't set up yet — uploads will show "Couldn't read".</strong>}
      </p>
      {!list.length ? <p className="muted">Nothing here.</p> : (
        <table>
          <thead><tr><th>Hotel</th><th>Month</th><th>File</th><th>Status</th><th /></tr></thead>
          <tbody>{list.map((s) => (
            <tr key={s.id} style={{ background: current?.id === s.id ? "var(--cream)" : undefined }}>
              <td>{s.hotelName}</td><td>{monthLabel(s.period)}</td>
              <td>{s.documentId ? <a href={`/api/documents/${s.documentId}/download`}>{s.fileName}</a> : "—"}</td>
              <td><span className={`pill ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span>{s.reviewer && <span className="muted" style={{ fontSize: 12 }}> · {s.reviewer}</span>}</td>
              <td className="num"><button className="btn link" onClick={() => setOpen(current?.id === s.id ? "" : s.id)}>{current?.id === s.id ? "Close" : "Open"}</button></td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {current && <Detail s={current} admin={admin} />}
    </div>
  );
}

function Detail({ s, admin }: { s: Stmt; admin: boolean }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const base = admin ? "/api/admin/statements" : "/api/statements";
  const act = useMutation({
    mutationFn: (what: "approve" | "reject" | "extract") => api(`${base}/${s.id}/${what}`, { method: "POST", body: { note } }),
    onSuccess: () => { setNote(""); qc.invalidateQueries({ queryKey: ["statements-review"] }); qc.invalidateQueries({ queryKey: ["pnl"] }); },
  });
  const cell = (v: number | null) => (v == null ? <span className="muted">—</span> : money(v));
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
      <h3 style={{ marginBottom: 6 }}>{s.hotelName} · {monthLabel(s.period)}</h3>
      {s.error && <p className="error">{s.error}</p>}
      {s.warnings.length > 0 && (
        <ul style={{ margin: "0 0 10px", paddingLeft: 18, color: "var(--warn)", fontSize: 13 }}>{s.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
      )}
      {s.status !== "failed" && s.status !== "extracting" && (
        <table className="fin">
          <thead><tr><th /><th className="num">Actual</th><th className="num">Budget</th><th className="num">Last year</th></tr></thead>
          <tbody>
            {s.rows.map((r) => r.kind === "header"
              ? <tr key={r.key} className="k-header"><td colSpan={4}>{r.label}</td></tr>
              : <tr key={r.key} className={r.kind === "line" ? "" : `k-${r.kind}`}><td>{r.label}</td><td className="num">{cell(r.actual)}</td><td className="num">{cell(r.budget)}</td><td className="num">{cell(r.lastYear)}</td></tr>)}
            <tr className="k-header"><td colSpan={4}>STATISTICS</td></tr>
            <tr><td>Rooms available</td><td className="num">{s.stats.roomsAvailable?.actual ?? "—"}</td><td className="num">{s.stats.roomsAvailable?.budget ?? "—"}</td><td className="num">{s.stats.roomsAvailable?.lastYear ?? "—"}</td></tr>
            <tr><td>Rooms sold</td><td className="num">{s.stats.roomsSold?.actual ?? "—"}</td><td className="num">{s.stats.roomsSold?.budget ?? "—"}</td><td className="num">{s.stats.roomsSold?.lastYear ?? "—"}</td></tr>
          </tbody>
        </table>
      )}
      {s.reviewNote && <p className="muted" style={{ fontSize: 13 }}>Note: {s.reviewNote}</p>}
      <div className="row" style={{ marginTop: 10 }}>
        {s.status === "extracted" && <>
          <input style={{ flex: 1, minWidth: 220 }} placeholder="Note (required to reject)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="btn gold" disabled={act.isPending} onClick={() => act.mutate("approve")}>Approve</button>
          <button className="btn danger" disabled={act.isPending || !note.trim()} onClick={() => act.mutate("reject")}>Reject</button>
        </>}
        <button className="btn secondary" disabled={act.isPending || s.status === "extracting" || !s.documentId} onClick={() => act.mutate("extract")}>{act.isPending && act.variables === "extract" ? "Reading…" : "Read again"}</button>
      </div>
      {act.error && <p className="error">{(act.error as Error).message}</p>}
    </div>
  );
}
